import { useEffect, useState } from "react";
import { Star, X, MessageSquareQuote } from "lucide-react";
import { api, formatApiError } from "../../lib/api";

const SERVICES = [
  "Excavation", "Concrete & Foundations", "Site Prep & Grading",
  "Decorative & Stamped Concrete", "Retaining Walls",
  "Driveways, Paving & Surfacing", "Drainage", "Other",
];

function Stars({ value, size = 15 }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} size={size} className={i <= value ? "text-[#2F5F80] fill-[#2F5F80]" : "text-[#2C3539]/20"} />
      ))}
    </div>
  );
}

export function HomeTestimonials() {
  const [reviews, setReviews] = useState([]);
  const [modal, setModal] = useState(false);

  useEffect(() => {
    api.get("/testimonials").then((r) => setReviews(r.data || [])).catch(() => {});
    if (new URLSearchParams(window.location.search).get("review") === "1") setModal(true);
  }, []);

  if (!reviews.length && !modal) return null;

  return (
    <section className="bg-white" data-testid="home-testimonials">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-16 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-7">
            <div className="text-[#2F5F80] text-xs tracking-[0.35em] font-bold uppercase mb-3">What Clients Say</div>
            <h2 className="font-display text-5xl sm:text-6xl md:text-7xl uppercase leading-[0.95] text-[#23282B]">Proof in their<br />own words.</h2>
          </div>
          <div className="lg:col-span-5 flex lg:justify-end">
            <button
              onClick={() => setModal(true)}
              data-testid="leave-review-btn"
              className="inline-flex items-center gap-2 bg-[#2F5F80] hover:bg-[#234B66] active:scale-95 text-white text-sm font-bold tracking-[0.15em] px-7 py-4 uppercase transition-all"
            >
              <MessageSquareQuote size={17} /> Leave a Review
            </button>
          </div>
        </div>

        <div className="mt-12 md:mt-16 grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="testimonials-grid">
          {reviews.slice(0, 6).map((t, i) => (
            <figure
              key={t.id}
              data-testid={`testimonial-${i}`}
              className={`border-2 p-7 md:p-8 flex flex-col gap-4 shadow-[6px_6px_0_#F1F3F5] ${i === 0 ? "bg-[#23282B] border-[#23282B]" : "bg-white border-[#2C3539]/10"}`}
            >
              <Stars value={t.rating} size={i === 0 ? 18 : 15} />
              <blockquote className={`leading-relaxed flex-1 ${i === 0 ? "text-white text-lg md:text-xl" : "text-[#23282B]/85 text-base"}`}>
                “{t.quote}”
              </blockquote>
              <figcaption>
                <div className={`font-display text-lg uppercase ${i === 0 ? "text-white" : "text-[#23282B]"}`}>{t.name}</div>
                <div className={`text-xs mt-0.5 ${i === 0 ? "text-[#9CC4DB]" : "text-[#23282B]/60"}`}>
                  {t.location}{t.location && t.service !== "Other" ? " · " : ""}{t.service !== "Other" ? t.service : ""}
                </div>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>

      {modal && <ReviewModal onClose={() => setModal(false)} />}
    </section>
  );
}

const inputCls = "w-full bg-white border-2 border-[#23282B]/15 focus:border-[#2F5F80] focus:outline-none px-3 py-3 text-sm text-[#23282B]";

function ReviewModal({ onClose }) {
  const [form, setForm] = useState({ name: "", location: "", service: "Other", rating: 5, quote: "" });
  const [state, setState] = useState({ busy: false, done: false, error: "" });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setState({ busy: true, done: false, error: "" });
    try {
      await api.post("/testimonials", form);
      setState({ busy: false, done: true, error: "" });
    } catch (err) {
      setState({ busy: false, done: false, error: formatApiError(err) });
    }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/60 flex items-center justify-center px-5 py-8 overflow-y-auto" data-testid="review-modal">
      <form onSubmit={submit} className="bg-white w-full max-w-lg p-7 border-t-4 border-[#2F5F80] my-auto max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <div className="font-display text-2xl uppercase text-[#23282B]">Leave a Review</div>
          <button type="button" onClick={onClose} data-testid="review-modal-close" className="p-1 text-[#23282B]/60 hover:text-[#23282B]"><X size={20} /></button>
        </div>

        {state.done ? (
          <div className="mt-6" data-testid="review-success">
            <div className="bg-green-50 border-l-4 border-green-600 px-4 py-4 text-sm text-green-800">
              Thanks for the kind words! Your review will appear on the site once our team approves it.
            </div>
            <button type="button" onClick={onClose} className="mt-5 bg-[#2F5F80] hover:bg-[#234B66] text-white text-sm font-bold tracking-[0.15em] px-6 py-3 uppercase transition-colors">Close</button>
          </div>
        ) : (
          <>
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label className="block"><span className="block text-xs tracking-[0.18em] font-bold uppercase text-[#23282B]/70 mb-1.5">Your Name *</span>
                <input data-testid="review-form-name" required value={form.name} onChange={set("name")} className={inputCls} /></label>
              <label className="block"><span className="block text-xs tracking-[0.18em] font-bold uppercase text-[#23282B]/70 mb-1.5">Location</span>
                <input data-testid="review-form-location" value={form.location} onChange={set("location")} className={inputCls} placeholder="e.g. Duncan, BC" /></label>
              <label className="block sm:col-span-2"><span className="block text-xs tracking-[0.18em] font-bold uppercase text-[#23282B]/70 mb-1.5">Service We Did For You</span>
                <select data-testid="review-form-service" value={form.service} onChange={set("service")} className={inputCls}>
                  {SERVICES.map((s) => <option key={s}>{s}</option>)}
                </select></label>
              <div className="sm:col-span-2">
                <span className="block text-xs tracking-[0.18em] font-bold uppercase text-[#23282B]/70 mb-1.5">Rating</span>
                <div className="flex gap-1" data-testid="review-form-rating">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <button type="button" key={i} onClick={() => setForm({ ...form, rating: i })} data-testid={`review-star-${i}`} className="p-1.5 active:scale-95 transition-transform">
                      <Star size={26} className={i <= form.rating ? "text-[#2F5F80] fill-[#2F5F80]" : "text-[#23282B]/25"} />
                    </button>
                  ))}
                </div>
              </div>
              <label className="block sm:col-span-2"><span className="block text-xs tracking-[0.18em] font-bold uppercase text-[#23282B]/70 mb-1.5">Your Review * (min 10 characters)</span>
                <textarea data-testid="review-form-quote" required minLength={10} rows={4} value={form.quote} onChange={set("quote")} className={inputCls} placeholder="How did the job go?" /></label>
            </div>
            {state.error && <div data-testid="review-form-error" className="mt-4 bg-red-50 border-l-4 border-red-500 px-3 py-2 text-sm text-red-700">{state.error}</div>}
            <button type="submit" disabled={state.busy} data-testid="review-form-submit" className="mt-6 bg-[#2F5F80] hover:bg-[#234B66] text-white text-sm font-bold tracking-[0.15em] px-7 py-3.5 uppercase transition-colors disabled:opacity-50">
              {state.busy ? "Sending…" : "Submit Review"}
            </button>
          </>
        )}
      </form>
    </div>
  );
}
